/**
 * Server Billing screen — staff-internal DigitalOcean cost + infrastructure
 * monitor. Ported from the legacy publisher-dashboard view
 * (src/views/admin/ServerBilling/index.jsx). Two tabs:
 *
 *   - Overview:       billing KPIs (MTD, projected, last month, daily rate)
 *                     + infrastructure resources table. Clicking a droplet
 *                     row drills into a live metrics panel (CPU / memory /
 *                     load, extracted from the latest DO monitoring sample).
 *   - Billing History: past invoices (latest 12). Clicking a row toggles an
 *                     inline line-item breakdown.
 *
 * Terminology is mirrored verbatim from the source — this is an internal
 * staff surface, so "droplet", "vCPU", "Month-to-Date", etc. stay as-is.
 *
 * Amounts use a local 2-decimal `fmt` (not lib/format's `formatUsd`)
 * deliberately: DigitalOcean billing figures are cent-precise and the
 * source rendered them at 2dp. `formatUsd` rounds values ≥ $1,000 to whole
 * dollars, which would corrupt invoice/cost precision. `fmt` matches the
 * legacy view exactly: `$X.XX` for numbers, `—` for null/undefined.
 */
import { useState } from 'react';
import { ExternalLink, Server, Boxes } from 'lucide-react';
import {
  DropletMetricsPayload,
  MetricSeries,
  OverviewResource
} from '@/api/serverBilling';
import {
  useServerForecast,
  useServerInvoiceDetail,
  useServerInvoices,
  useServerMetrics,
  useServerOverview
} from '@/hooks/useServerBilling';
import { BackButton, Card, Skeleton } from '@/components/ui/atoms';

// Local cent-precise formatter — mirrors the legacy view's `fmt`.
const fmt = (n: number | null | undefined): string =>
  n != null ? `$${Number(n).toFixed(2)}` : '—';

// ─── Resource status dot ──────────────────────────────────────────────────

function statusColor(status: string): string {
  if (status === 'active' || status === 'running') return 'bg-emerald-500';
  if (status === 'off' || status === 'error') return 'bg-red-500';
  return 'bg-amber-500';
}

// ─── Overview tab ───────────────────────────────────────────────────────────

function OverviewTab() {
  const { data: overview, isLoading } = useServerOverview();
  const { data: forecast } = useServerForecast();
  const [selectedDroplet, setSelectedDroplet] = useState<string | null>(null);

  const balance = overview?.balance;
  const resources = overview?.resources ?? [];

  const mom = forecast?.monthOverMonth;
  const momNum = mom != null ? parseFloat(mom) : null;
  const momColor =
    momNum != null && momNum > 0 ? 'text-red-400' : 'text-emerald-300';
  const momLabel =
    mom != null ? `${momNum != null && momNum > 0 ? '+' : ''}${mom}% vs last month` : '';

  return (
    <div className="space-y-6">
      {/* Billing KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <BillingStat
          label="Month-to-Date"
          value={fmt(balance?.mtdUsage)}
          sub={`Day ${forecast?.daysElapsed ?? '—'} of ${forecast?.daysInMonth ?? '—'}`}
          loading={isLoading}
        />
        <BillingStat
          label="Projected Total"
          value={fmt(forecast?.projected)}
          sub={momLabel ? <span className={momColor}>{momLabel}</span> : undefined}
          loading={isLoading}
        />
        <BillingStat
          label="Last Month"
          value={fmt(forecast?.lastMonthTotal)}
          loading={isLoading}
        />
        <BillingStat
          label="Daily Rate"
          value={fmt(forecast?.dailyRate)}
          sub={`${overview?.totalResources ?? 0} resources`}
          loading={isLoading}
        />
      </div>

      {/* Resources table */}
      <Card className="overflow-hidden p-0">
        <div className="border-b border-nurev-border p-5">
          <h2 className="text-sm font-semibold text-nurev-text">
            Infrastructure Resources
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-xs text-nurev-muted">
                <th className="p-3 text-left font-medium">Name</th>
                <th className="p-3 text-left font-medium">Type</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Spec</th>
                <th className="p-3 text-left font-medium">Region</th>
                <th className="p-3 text-left font-medium">IP</th>
                <th className="p-3 text-right font-medium">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-nurev-muted">
                    Loading...
                  </td>
                </tr>
              ) : resources.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-nurev-muted">
                    No resources found
                  </td>
                </tr>
              ) : (
                resources.map(r => (
                  <ResourceRow
                    key={String(r.id)}
                    resource={r}
                    onSelect={() =>
                      r.type === 'droplet'
                        ? setSelectedDroplet(String(r.id))
                        : undefined
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Droplet metrics (expandable) */}
      {selectedDroplet && (
        <DropletMetrics
          dropletId={selectedDroplet}
          onClose={() => setSelectedDroplet(null)}
        />
      )}
    </div>
  );
}

function BillingStat({
  label,
  value,
  sub,
  loading
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card className="p-4 lg:p-5">
      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-nurev-muted lg:text-xs">
        {label}
      </p>
      {loading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <>
          <p className="text-lg font-bold leading-tight text-nurev-text lg:text-2xl">
            {value}
          </p>
          {sub && <p className="mt-1 text-[10px] text-nurev-muted lg:text-xs">{sub}</p>}
        </>
      )}
    </Card>
  );
}

function ResourceRow({
  resource: r,
  onSelect
}: {
  resource: OverviewResource;
  onSelect: () => void;
}) {
  const isDroplet = r.type === 'droplet';
  return (
    <tr
      onClick={onSelect}
      className={`border-b border-nurev-border/50 hover:bg-white/[0.03] ${
        isDroplet ? 'cursor-pointer' : ''
      }`}
    >
      <td className="p-3 font-medium text-nurev-text">{r.name}</td>
      <td className="p-3">
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            isDroplet
              ? 'bg-sky-500/20 text-sky-300'
              : 'bg-purple-500/20 text-purple-300'
          }`}
        >
          {r.type}
        </span>
      </td>
      <td className="p-3">
        <span className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${statusColor(r.status)}`} />
          <span className="text-xs text-nurev-text-soft">{r.status}</span>
        </span>
      </td>
      <td className="p-3 text-xs text-nurev-muted">{r.spec}</td>
      <td className="p-3 text-xs text-nurev-muted">{r.region}</td>
      <td className="p-3 font-mono text-xs text-nurev-muted">{r.ip || '—'}</td>
      <td className="p-3 text-right text-nurev-text">
        {r.monthlyCost > 0 ? fmt(r.monthlyCost) : '—'}
      </td>
    </tr>
  );
}

// ─── Droplet metrics panel ────────────────────────────────────────────────

// Pull the latest sample value out of a DO monitoring series.
function extractLatest(metric: MetricSeries | null | undefined): number | null {
  const values = metric?.data?.result?.[0]?.values;
  if (!values || values.length === 0) return null;
  return parseFloat(values[values.length - 1][1]);
}

function DropletMetrics({
  dropletId,
  onClose
}: {
  dropletId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useServerMetrics(dropletId);
  const metrics: Partial<DropletMetricsPayload> = data ?? {};

  const cpuVal = extractLatest(metrics.cpu);
  const memVal = extractLatest(metrics.memory);
  const loadVal = extractLatest(metrics.load);

  return (
    <Card className="border-amber-500/30 p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-nurev-text">
          Metrics — Droplet {dropletId}
        </h3>
        <BackButton onClick={onClose} label="Close" />
      </div>
      {isLoading ? (
        <div className="flex h-20 items-center justify-center text-nurev-muted">
          Loading metrics...
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCell
            label="CPU Usage"
            value={cpuVal != null ? `${(cpuVal * 100).toFixed(1)}%` : '—'}
          />
          <MetricCell
            label="Memory Free"
            value={memVal != null ? `${(memVal / 1e9).toFixed(1)} GB` : '—'}
          />
          <MetricCell
            label="Load (1m)"
            value={loadVal != null ? loadVal.toFixed(2) : '—'}
          />
          <div>
            <p className="mb-1 text-xs text-nurev-muted">Last 24h</p>
            <p className="text-xs text-nurev-muted">
              Time-series charts coming soon.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-1 text-xs text-nurev-muted">{label}</p>
      <p className="text-lg font-bold text-nurev-text">{value}</p>
    </div>
  );
}

// ─── Billing History (invoices) tab ─────────────────────────────────────────

function InvoicesTab() {
  const { data, isLoading } = useServerInvoices();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const invoices = data?.invoices ?? [];

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-xs text-nurev-muted">
                <th className="p-3 text-left font-medium">Period</th>
                <th className="p-3 text-right font-medium">Amount</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">UUID</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-nurev-muted">
                    Loading...
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-nurev-muted">
                    No invoices yet
                  </td>
                </tr>
              ) : (
                invoices.slice(0, 12).map(inv => {
                  const status = inv.status || 'paid';
                  return (
                    <tr
                      key={inv.invoice_uuid}
                      onClick={() =>
                        setSelectedUuid(
                          selectedUuid === inv.invoice_uuid
                            ? null
                            : inv.invoice_uuid
                        )
                      }
                      className="cursor-pointer border-b border-nurev-border/50 hover:bg-white/[0.03]"
                    >
                      <td className="p-3 text-nurev-text">
                        {inv.invoice_period}
                      </td>
                      <td className="p-3 text-right font-semibold text-nurev-text">
                        ${inv.amount}
                      </td>
                      <td className="p-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            status === 'paid'
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : 'bg-amber-500/20 text-amber-300'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-xs text-nurev-muted">
                        {inv.invoice_uuid?.slice(0, 8)}...
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedUuid && <InvoiceDetail uuid={selectedUuid} />}
    </div>
  );
}

function InvoiceDetail({ uuid }: { uuid: string }) {
  const { data, isLoading } = useServerInvoiceDetail(uuid);
  const items = data?.invoice_items ?? [];

  return (
    <Card className="overflow-hidden border-amber-500/30 p-0">
      <div className="border-b border-nurev-border p-4">
        <h3 className="text-sm font-semibold text-nurev-text">
          Invoice Line Items
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-nurev-border text-xs text-nurev-muted">
              <th className="p-3 text-left font-medium">Resource</th>
              <th className="p-3 text-left font-medium">Description</th>
              <th className="p-3 text-right font-medium">Amount</th>
              <th className="p-3 text-right font-medium">Duration</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="p-4 text-center text-nurev-muted">
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-4 text-center text-nurev-muted">
                  No line items
                </td>
              </tr>
            ) : (
              items.map((item, i) => (
                <tr key={i} className="border-b border-nurev-border/50">
                  <td className="p-3 text-xs text-nurev-text">
                    {item.resource_name || item.product}
                  </td>
                  <td className="p-3 text-xs text-nurev-muted">
                    {item.description}
                  </td>
                  <td className="p-3 text-right text-nurev-text">
                    ${item.amount}
                  </td>
                  <td className="p-3 text-right text-xs text-nurev-muted">
                    {item.duration || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', icon: Server },
  { id: 'invoices', label: 'Billing History', icon: Boxes }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function ServerBillingScreen() {
  const [tab, setTab] = useState<Tab>('overview');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Server Billing
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Infrastructure costs, resource status, and performance metrics.
            Overview auto-refreshes every 60 seconds.
          </p>
        </div>
        <a
          href="https://app.nurev.io/server-billing"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'invoices' && <InvoicesTab />}
    </div>
  );
}
